<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['close', 'execute']);

const { t } = useI18n();
const store = useStore();

const macros = useMapGetter('macros/getMacros');
const uiFlags = useMapGetter('macros/getUIFlags');

const query = ref('');

const filteredMacros = computed(() => {
  if (!query.value) return macros.value;
  return macros.value.filter(macro =>
    macro.name.toLowerCase().includes(query.value.toLowerCase())
  );
});

const hasMacros = computed(() => macros.value.length > 0);
const hasFilteredMacros = computed(() => filteredMacros.value.length > 0);

onMounted(() => {
  if (!hasMacros.value && !uiFlags.value.isFetching) {
    store.dispatch('macros/get');
  }
});

const onClose = () => {
  emit('close');
};

const handleSelect = macro => {
  emit('execute', macro);
};
</script>

<template>
  <div
    v-on-click-outside="onClose"
    class="absolute ltr:right-2 rtl:left-2 top-12 origin-top-right z-20 w-60 bg-n-alpha-3 backdrop-blur-[100px] border-n-weak rounded-lg border border-solid shadow-md"
    role="dialog"
    aria-labelledby="macro-dialog-title"
  >
    <div class="triangle">
      <svg height="12" viewBox="0 0 24 12" width="24">
        <path d="M20 12l-8-8-12 12" fill-rule="evenodd" stroke-width="1px" />
      </svg>
    </div>
    <div class="flex items-center justify-between p-2.5">
      <span class="text-sm font-medium">{{
        t('BULK_ACTION.MACROS.PICK_MACRO')
      }}</span>
      <NextButton ghost xs slate icon="i-lucide-x" @click="onClose" />
    </div>
    <div class="flex flex-col max-h-60 min-h-0">
      <header v-if="hasMacros" class="py-2 px-2.5">
        <Input
          v-model="query"
          type="search"
          :placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
          icon-left="i-lucide-search"
          size="sm"
          class="w-full"
          :aria-label="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
        />
      </header>
      <ul
        v-if="hasMacros"
        class="flex-1 overflow-y-auto m-0 list-none"
        role="listbox"
        :aria-label="t('BULK_ACTION.MACROS.PICK_MACRO')"
      >
        <li v-if="!hasFilteredMacros" class="p-2 text-center">
          <span class="text-sm text-n-slate-11">{{
            t('BULK_ACTION.MACROS.NO_MACROS_FOUND')
          }}</span>
        </li>
        <li
          v-for="macro in filteredMacros"
          :key="macro.id"
          class="my-1 mx-0 py-0 px-2.5"
          role="option"
        >
          <button
            type="button"
            class="items-center rounded-md cursor-pointer flex py-1.5 px-2.5 w-full text-left hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
            @click="handleSelect(macro)"
          >
            <span
              class="i-lucide-zap size-4 ltr:mr-2 rtl:ml-2 text-n-slate-11 flex-shrink-0"
            />
            <span
              class="overflow-hidden flex-grow text-sm whitespace-nowrap text-ellipsis"
            >
              {{ macro.name }}
            </span>
          </button>
        </li>
      </ul>
      <div v-else class="p-3 text-center">
        <span class="text-sm text-n-slate-11">{{
          uiFlags.isFetching
            ? t('BULK_ACTION.AGENT_LIST_LOADING')
            : t('BULK_ACTION.MACROS.NO_MACROS_CONFIGURED')
        }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.triangle {
  @apply block z-10 absolute text-left -top-3 ltr:right-[--triangle-position] rtl:left-[--triangle-position];

  svg path {
    @apply fill-n-alpha-3 backdrop-blur-[100px] stroke-n-weak;
  }
}
</style>
